import { NotFoundError } from '@evogard/ui';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  CoreVehicleMatch,
  ThirdParty,
  ThirdPartyVehicle,
  ThirdPartyWithVehicles,
} from '@evogard/contract';
import { loadFailureMessage, normalizePhone, normalizePlate } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';

/**
 * Toda conversa da interna com o cadastro de terceiros passa por aqui.
 *
 * É o "composable" do terceiro: a busca, o detalhe, o cadastro da pessoa e do
 * veículo, a correção e o arquivamento — com as regras de consulta da casa já
 * dentro, para que nenhuma tela que use o terceiro precise lembrar delas:
 *
 * - **a praça está na chave** (`tenantId`), e o resultado anterior só é
 *   segurado dentro da mesma praça — trocar de base e manter a lista antiga
 *   faria a tela nomear a base nova sobre os terceiros da anterior;
 * - **o primeiro segmento é `sinistros`**, o id do módulo no menu, e não a
 *   pasta (`claims`) — é ele que `invalidateQueries` usa;
 * - **toda escrita invalida a busca e o detalhe juntos**: um veículo arquivado
 *   no detalhe precisa sumir da busca de onde se escolhe para o sinistro.
 *
 * Esconder não é proteger: `permissions` só decide o que a tela oferece. Quem
 * protege é o `PermissionGuard` da API.
 */

const ROOT = ['sinistros', 'terceiros'] as const;

/** Busca começa em 2 letras — antes disso, casaria quase todo mundo. */
export const MIN_SEARCH = 2;

export function useThirdPartyPermissions() {
  const { data: context } = useSessionContext();
  return {
    canView: can(context, 'claims.third_parties', 'ver'),
    canCreate: can(context, 'claims.third_parties', 'criar'),
    canEdit: can(context, 'claims.third_parties', 'editar'),
  };
}

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

/** A busca por nome, telefone ou placa, com os veículos ATIVOS de cada terceiro. */
export function useThirdPartySearch(
  term: string,
  options: {
    page?: number;
    perPage?: number;
    enabled?: boolean;
    /** `sim`: só quem tem veículo ativo; `nao`: só quem não tem. */
    withVehicle?: 'sim' | 'nao';
  } = {},
) {
  const tenantId = useTenantId();
  const { page = 1, perPage = 10, enabled = true, withVehicle } = options;
  const search = term.trim();

  return useQuery({
    queryKey: [...ROOT, 'busca', tenantId, search, withVehicle ?? null, page, perPage],
    queryFn: async () => {
      const r = await api.claims.listThirdParties({
        query: {
          page,
          perPage,
          ...(search ? { search } : {}),
          ...(withVehicle ? { withVehicle } : {}),
        },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os terceiros'));
      return r.body;
    },
    enabled: enabled && tenantId !== undefined,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? keepPreviousData(previous) : undefined,
  });
}

/** Um terceiro e TODOS os veículos dele, os arquivados inclusive. */
export function useThirdParty(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'detalhe', tenantId, id],
    queryFn: async () => {
      const r = await api.claims.getThirdParty({ params: { id: id! } });
      if (r.status === 404)
        throw new NotFoundError('Este terceiro não existe nesta base');
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o terceiro'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/**
 * Quem já tem este telefone ou esta placa — para AVISAR antes de cadastrar.
 *
 * O documento do terceiro é opcional, e duas linhas para a mesma pessoa não
 * se desfazem sozinhas. A decisão de 24/09 é avisar e deixar cadastrar mesmo
 * assim: pode ser um parente com o mesmo número, e a placa não é única de
 * propósito (o mesmo carro aparece em sinistros diferentes).
 *
 * Só pergunta quando o valor já normaliza — um telefone pela metade casaria
 * com meia base. `exceptId` tira da resposta o próprio terceiro que está sendo
 * editado.
 */
export function useSimilarThirdParties(
  input: { phone?: string; plate?: string },
  exceptId?: number,
) {
  const phone = normalizePhone(input.phone ?? '');
  const plate = normalizePlate(input.plate ?? '');
  const byPhone = useThirdPartySearch(phone ?? '', {
    perPage: 5,
    enabled: phone !== null,
  });
  const byPlate = useThirdPartySearch(plate ?? '', {
    perPage: 5,
    enabled: plate !== null,
  });

  const samePhone = phone
    ? (byPhone.data?.items ?? []).filter((t) => t.phone === phone && t.id !== exceptId)
    : [];
  const samePlate = plate
    ? (byPlate.data?.items ?? []).filter(
        (t) => t.id !== exceptId && t.vehicles.some((v) => v.plate === plate),
      )
    : [];
  return { samePhone, samePlate };
}

/**
 * Os carros do núcleo com esta placa — o terceiro que também é associado.
 * Só pergunta quando a placa já normaliza; o vínculo é oferecido, nunca feito
 * sozinho.
 */
export function useCoreVehiclesByPlate(rawPlate: string) {
  const tenantId = useTenantId();
  const plate = normalizePlate(rawPlate);
  return useQuery({
    queryKey: [...ROOT, 'nucleo-por-placa', tenantId, plate],
    queryFn: async (): Promise<CoreVehicleMatch[]> => {
      const r = await api.claims.findCoreVehiclesByPlate({ body: { plate: plate! } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os veículos do cadastro da casa'));
      return r.body;
    },
    enabled: plate !== null && tenantId !== undefined,
  });
}

export interface ThirdPartyInput {
  name: string;
  phone: string;
  email: string | null;
  document: string | null;
}

export interface VehicleInput {
  plate: string;
  model: string;
  manufactureYear: number;
  modelYear: number;
  /** O carro do núcleo com a mesma placa, quando quem cadastra confirma. */
  vehicleId: number | null;
}

/** As escritas. Cada uma devolve o registro gravado — a tela seleciona o que acabou de nascer. */
export function useThirdPartyMutations() {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: [...ROOT] });

  const create = useMutation({
    mutationFn: async (body: ThirdPartyInput): Promise<ThirdParty> =>
      ok(
        await api.claims.createThirdParty({ body }),
        201,
        'Não foi possível cadastrar o terceiro.',
      ),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: Partial<ThirdPartyInput>;
    }): Promise<ThirdParty> =>
      ok(
        await api.claims.updateThirdParty({ params: { id }, body }),
        200,
        'Não foi possível corrigir o terceiro.',
      ),
    onSuccess: refresh,
  });

  const createVehicle = useMutation({
    mutationFn: async ({
      thirdPartyId,
      body,
    }: {
      thirdPartyId: number;
      body: VehicleInput;
    }): Promise<ThirdPartyVehicle> =>
      ok(
        await api.claims.createThirdPartyVehicle({ params: { id: thirdPartyId }, body }),
        201,
        'Não foi possível cadastrar o veículo.',
      ),
    onSuccess: refresh,
  });

  const setVehicleArchived = useMutation({
    mutationFn: async ({
      thirdPartyId,
      vehicleId,
      archived,
    }: {
      thirdPartyId: number;
      vehicleId: number;
      archived: boolean;
    }): Promise<ThirdPartyVehicle> =>
      ok(
        await api.claims.updateThirdPartyVehicle({
          params: { id: thirdPartyId, vehicleId },
          body: { archived },
        }),
        200,
        archived
          ? 'Não foi possível arquivar o veículo.'
          : 'Não foi possível reativar o veículo.',
      ),
    onSuccess: refresh,
  });

  return { create, update, createVehicle, setVehicleArchived };
}

export type { CoreVehicleMatch, ThirdParty, ThirdPartyVehicle, ThirdPartyWithVehicles };
